﻿import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';

import { Overlay } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { Extent } from 'ol/extent';
import Map from 'ol/Map';

import { Badge, BadgeProps, Button, Col, Popover, Row } from 'antd';
import Tooltip, { RenderFunction, TooltipPlacement } from 'antd/lib/tooltip';

import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Utilities } from 'msg-ui-react/dist/utils/Utilities';

import { isCoordinate } from '../geometries/models/Coordinates';
import { DetectEvent } from '../interactions/DetectEvent';
import DateLineUtilities from '../utilities/DateLineUtilities';

export interface IMapPopupTarget {
    coordinate?: Coordinate | DetectEvent,
    offset?: [number, number],
    title?: React.ReactNode | RenderFunction,
    content?: React.ReactNode | RenderFunction,
}

interface Props {
    map: Map | undefined,
    /** Position and title/content of popup. Set to undefined to hide popup. */
    target?: IMapPopupTarget,
    /** Shows a small circle at target location. Default false. */
    anchor?: BadgeProps | boolean,
    /** By default a Popover component is shown */
    asTooltip?: boolean,
    /** Default 240px */
    maxWidth?: number,
    /** Default 180px */
    minWidth?: number,
    /** Add a close button and call function on click */
    closeBtn?: React.MouseEventHandler<HTMLElement>,
    style?: CSSProperties
}

/**
 * Renders an Ant design Popover (or Tooltip) component which can be placed on an openlayers map.
 * Component will be added to map as an overlay.
 * Component becomes visible when target property is set.
 * @param param0
 */
const MapPopup: React.FC<Props> = ({
    map,
    anchor,
    target,
    asTooltip,
    closeBtn,
    style,
    minWidth = 180,
    maxWidth = 240
}) => {

    //#region States

    const [visible, setVisible] = useState(false);
    const [placement, setPlacement] = useState<TooltipPlacement>('top');
    const [extent, setExtent] = useState<Extent>();
    const [width, setWidth] = useState<number>(0);
    const [height, setHeight] = useState<number>(0);

    const containerRef = useRef<HTMLDivElement>(null);
    const elementRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<Overlay>();
    const timeoutRef = useRef<number>();

    //#endregion

    //#region Effects   
    useEffect(() => {
        if (map && elementRef.current) {

            const element = elementRef.current;            
            const overlay = new Overlay({ element, insertFirst: false, stopEvent: false });
            map.addOverlay(overlay);

            const parent = overlay.getElement()?.parentElement;
            if (parent)
                parent.style.pointerEvents = 'none';

            overlayRef.current = overlay;

            map.on("moveend", updateExtent);           

            return () => {
                map.un("moveend", updateExtent);
                map.removeOverlay(overlay);
                window.clearTimeout(timeoutRef.current);
            }
        }
    }, [map]);

    useEffect(() => {
        if (visible && elementRef.current) {
            const { offsetHeight, offsetWidth } = elementRef.current;
            setWidth(offsetWidth);
            setHeight(offsetHeight);
        }            
    }, [visible, maxWidth, minWidth]);

    useEffect(() => {
        if (map && overlayRef.current) {
            const overlay = overlayRef.current;
            const newPosition = calculatePosition(map, target);
            if (newPosition) {
                const newPlacement = calculateTooltipPlacement(map, newPosition, width, height, target?.offset);
                if (newPlacement) {
                    const newOffset = calculateOverlapOffset(newPlacement, target?.offset);
                    const currentPosition = overlay.getPosition();
                    const currentOffset = overlay.getOffset();

                    if ((!currentPosition || !Utilities.compareArrays(newPosition, currentPosition)) ||
                        (!currentOffset || !Utilities.compareArrays(newOffset, currentOffset)) ||
                        newPlacement !== placement) {

                        setVisible(false);
                        overlay.setPosition(newPosition);
                        overlay.setOffset(newOffset)
                        setPlacement(newPlacement);

                        window.clearTimeout(timeoutRef.current);
                        timeoutRef.current = window.setTimeout(() => {
                            setVisible(true);
                        }, 50);
                    }
                }
            }
            else {
                window.clearTimeout(timeoutRef.current);
                setVisible(false);
                overlay.setPosition(undefined);
            }
        }
    }, [map, target, width, height, extent]);   

    const updateExtent = () => {
        if (map)
            setExtent(map.getView().calculateExtent(map.getSize()));
    }    

    const renderReactNodeLikeValue = (
        value: string | number | true | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactFragment | React.ReactPortal | RenderFunction
    ): ReactNode => {
        if (typeof value === 'function') {
            return value();
        }
        return value;
    }

    //#endregion

    return (
        // Outer container prevents React DOMException when component is removed
        // https://stackoverflow.com/questions/54880669/react-domexception-failed-to-execute-removechild-on-node-the-node-to-be-re
        <div ref={containerRef} style={{
            display: 'none'
        }}>
            {
                // Inner container is moved to ol overlay container
            }

            <div ref={elementRef} style={{
                opacity: 0.9,
                ...style
            }}>
                {
                    visible && !asTooltip && <Popover
                        open={true}
                        arrowPointAtCenter={true}
                        autoAdjustOverflow={false}
                        placement={placement}
                        title={
                            target?.title && <Row wrap={false} style={{ maxWidth, minWidth }}>
                                <Col flex="auto">
                                    {renderReactNodeLikeValue(target.title)}
                                </Col>
                                {
                                    closeBtn &&
                                    <Col flex="none">
                                            <Button
                                                size="small" type="text"
                                                style={{ left: 8, pointerEvents: 'all' }}
                                                onClick={closeBtn}
                                                icon={<FontAwesomeIcon icon={faTimes} />}
                                            />
                                    </Col>
                                }
                            </Row>
                        }
                        content={target?.content}
                        destroyTooltipOnHide={false}
                        getPopupContainer={() => elementRef.current!}
                    />
                }
                {
                    visible && asTooltip && <Tooltip
                        open={true}
                        arrowPointAtCenter={true}
                        autoAdjustOverflow={false}
                        placement={placement}
                        title={
                            target?.title && <Row wrap={false} style={{ maxWidth, minWidth }}>
                                <Col flex="auto">
                                    {renderReactNodeLikeValue(target.title)}
                                </Col>
                            </Row>
                        }
                        destroyTooltipOnHide={false}
                        getPopupContainer={() => elementRef.current!}
                    />
                }   
                {
                    visible && anchor && <>
                        {
                            anchor == true ?
                                <Badge status="processing" style={{ transform: 'translate(-50%, -50%)' }} /> :
                                <Badge {...anchor} style={{ transform: 'translate(-50%, -50%)', ...anchor.style }} />
                        }
                    </>
                }
            </div>
        </div>
    )
}

export default MapPopup;

function calculateOverlapOffset(placement: TooltipPlacement, offset?: [number, number]) {
    if (offset) {
        var x = 0; var y = 0;
        if (placement.indexOf("top") === 0)
            y = -offset[1];
        else if (placement.indexOf("bottom") === 0)
            y = offset[1];

        if (placement.indexOf("left") === 0)
            x = -offset[0];
        else if (placement.indexOf("right") === 0)
            x = offset[0];
        return [x, y];
    }
    return [0, 0];
}

function calculateTooltipPlacement(map: Map, position: Coordinate, width: number, height: number, offset?: [number, number]): TooltipPlacement | undefined {
    const target = map.getTargetElement();
    const pixel = map.getPixelFromCoordinate(position);

    if (target && pixel?.length > 1) {
        const { clientWidth } = target;
        const [x, y] = pixel;
        const [offsetX, offsetY] = offset ?? [0, 0];

        const safeMargin = 24;
        const xmargin = 0.5 * width + safeMargin + offsetX;
        const useBottom = y < (height + safeMargin + offsetY);

        if (x < safeMargin)
            return "right";
        else if (x < xmargin)
            return useBottom ? "bottomLeft" : "topLeft";
        else if (x < (clientWidth - xmargin))
            return useBottom ? "bottom" : "top";
        else if (x < (clientWidth - safeMargin))
            return useBottom ? "bottomRight" : "topRight";
        else
            return "left";
    }

    return undefined;
}

function calculatePosition(map: Map, target?: IMapPopupTarget) {
    if (target?.coordinate) {
        var position: Coordinate;
        if (target.coordinate instanceof DetectEvent) {
            const evt = target.coordinate;
            position = evt.mapBrowserEvent.coordinate;
            if (evt.coordinate && evt.coordinate.pixelDistance < 10) {
                //console.log(evt.coordinate.pixelDistance, evt.coordinate.coordinates, evt.coordinate.coordinates)
                const point = isCoordinate(evt.coordinate.coordinates);
                if (point)
                    position = point;
                else if (evt.coordinate.interpolatedPoint)
                    position = evt.coordinate.interpolatedPoint;
            }
        }
        else
            position = target.coordinate;

        return DateLineUtilities.getPositionWithinMapViewExtent(map, position)
    }
    return undefined;
}